import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom, Observable } from 'rxjs';

import PaginationResult from '../../../core/domain/pagination-result';
import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { GenericException } from '../../../core/logic/exception';
import { HelpMeCategories } from '../../../ui/help-me/help-me-categories';
import { AttachmentUpload } from '../domain/attachment-upload';
import {
  MessageAttachmentHelpProps,
  MessageHelp,
  MessageThreadHelpProps,
} from '../domain/message-help';

export interface MessageAttachmentHelpSchema {
  name: string;
  path: string;
  size: number;
  mimeType: string;
  url: string | null;
}

export interface BaseMessageHelpSchema {
  createdAt: Timestamp;
  content: string;
  attachments: MessageAttachmentHelpSchema[] | null;
}

export interface MessageThreadHelpSchema extends BaseMessageHelpSchema {
  supportReply: boolean;
}

export interface MessageHelpSchema extends BaseMessageHelpSchema {
  dietitianId: string | null;
  dietitianFullName: string | null;
  category: HelpMeCategories;
  answered: boolean;
  closed: boolean;
  thread: MessageThreadHelpSchema[] | null;
}

@Injectable()
export class MessageHelpRepository {
  private readonly searchMessageHelpFromCloud: (
    data: unknown,
  ) => Observable<PaginationResult<{ [key: string]: unknown }>>;

  private static readonly MESSAGE_HELP_INDEX = 'message_help';

  constructor(
    private firestore: AngularFirestore,
    private functions: AngularFireFunctions,
    private storage: AngularFireStorage,
  ) {
    this.searchMessageHelpFromCloud = this.functions.httpsCallable<
      unknown,
      PaginationResult<{ [key: string]: unknown }>
    >('message_help-searchMessageHelp');
  }

  private collection(queryFn?: QueryFn) {
    return this.firestore.collection<MessageHelpSchema>(
      'message_helps',
      queryFn,
    );
  }

  private toSchema(entity: MessageHelp): MessageHelpSchema {
    return <MessageHelpSchema>{
      dietitianId: entity.dietitianId ?? null,
      dietitianFullName: entity.dietitianFullName ?? null,
      category: entity.category,
      answered: entity.answered,
      closed: entity.closed,
      content: entity.content,
      createdAt: Timestamp.now(),
      attachments: this.toAttachmentsSchema(entity.attachments),
      thread: this.toThreadSchema(entity.thread),
    };
  }

  private toAttachmentsSchema(
    schema: MessageAttachmentHelpProps[] | undefined,
  ) {
    if (schema) {
      return schema.map((value) => {
        return {
          name: value.name,
          size: value.size,
          mimeType: value.mimeType,
          path: value.path,
          url: value.url ?? null,
        } as MessageAttachmentHelpSchema;
      });
    }
    return null;
  }

  private toThreadSchema(schema: MessageThreadHelpProps[] | undefined) {
    if (schema) {
      return schema.map((value) => {
        return {
          createdAt: Timestamp.fromDate(value.createdAt),
          content: value.content,
          supportReply: value.supportReply,
          attachments: this.toAttachmentsSchema(value.attachments),
        } as MessageThreadHelpSchema;
      });
    }
    return null;
  }

  public async create(message: MessageHelp) {
    await this.collection().add(this.toSchema(message));
  }

  public async update(message: MessageHelp) {
    await this.collection()
      .doc(message.id.toString())
      .set(this.toSchema(message));
  }

  public uploadAttachement(
    attachment: MessageAttachmentHelpProps,
  ): AttachmentUpload {
    try {
      if (attachment.path == null || attachment.file == null) {
        throw new GenericException();
      }
      const fileRef = this.storage.ref(attachment.path);
      const task = fileRef.put(attachment.file, {
        contentType: attachment.mimeType,
      });

      return AttachmentUpload.create({
        attachment,
        task,
      });
    } catch (e) {
      console.error('🔴 uploadAttachement', e);
      throw e;
    }
  }

  async load(messageHelpId: string): Promise<MessageHelp> {
    try {
      const rs = await firstValueFrom(
        this.collection().doc(messageHelpId).get(),
      );
      return this.fromSchema(rs.data() as MessageHelpSchema, rs.id);
    } catch (e) {
      console.error('🔴 MessageHelpRepo.load()', e);
      throw e;
    }
  }

  async findAll(
    dietitianId: string | undefined,
  ): Promise<MessageHelp[] | undefined> {
    try {
      if (dietitianId) {
        const rs = await firstValueFrom(
          this.collection((ref) =>
            ref
              .where('dietitianId', '==', dietitianId)
              .where('closed', '==', false)
              .orderBy('createdAt', 'desc'),
          ).get(),
        );
        return rs.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
      }
      return undefined;
    } catch (e) {
      console.error('🔴 MessageHelpRepo.findAll()', e);
      throw e;
    }
  }

  async searchMessages(
    answered: string | undefined,
    closed: string | undefined,
    category: HelpMeCategories | undefined,
    query: string,
    resultsPerPage: number,
    page: number,
  ): Promise<PaginationResult<MessageHelp>> {
    const index = MessageHelpRepository.MESSAGE_HELP_INDEX;
    const results = await firstValueFrom(
      this.searchMessageHelpFromCloud({
        answered,
        closed,
        category,
        query,
        index,
        resultsPerPage,
        page,
      }),
    );
    return {
      ...results,
      results: results.results.map(this.fromMap),
    };
  }

  private fromSchema(
    schema: MessageHelpSchema,
    id?: string | undefined,
  ): MessageHelp {
    return MessageHelp.create(
      {
        dietitianId: schema.dietitianId ?? undefined,
        dietitianFullName: schema.dietitianFullName ?? undefined,
        createdAt: schema.createdAt.toDate(),
        category: schema.category,
        answered: schema.answered ?? false,
        closed: schema.closed,
        content: schema.content,
        attachments: this.fromAttachementsSchema(schema.attachments),
        thread: this.fromThreadSchema(schema.thread),
      },
      new UniqueEntityID(id),
    );
  }

  private fromAttachementsSchema(
    attachments: MessageAttachmentHelpSchema[] | null,
  ): MessageAttachmentHelpProps[] | undefined {
    if (attachments && attachments.length > 0) {
      return attachments.map((a) => {
        return {
          name: a.name,
          size: a.size,
          mimeType: a.mimeType,
          path: a.path,
          url: a.url,
        } as MessageAttachmentHelpProps;
      });
    }
    return undefined;
  }

  private fromThreadSchema(
    thread: MessageThreadHelpSchema[] | null,
  ): MessageThreadHelpProps[] | undefined {
    if (thread && thread.length > 0) {
      return thread.map((t) => {
        return {
          createdAt: t.createdAt.toDate(),
          content: t.content,
          supportReply: t.supportReply,
          attachments: this.fromAttachementsSchema(t.attachments),
        } as MessageThreadHelpProps;
      });
    }
    return undefined;
  }

  private fromMap(map: { [key: string]: unknown }): MessageHelp {
    const getDate = (data: unknown) =>
      typeof data === 'string' ? new Date(data) : new Date();

    return MessageHelp.create(
      {
        dietitianFullName: map['dietitianFullName'] as string,
        category: map['category'] as HelpMeCategories,
        content: map['content'] as string,
        answered: map['answered'] as boolean,
        closed: map['closed'] as boolean,
        createdAt: getDate(map['createdAt']),
      },
      new UniqueEntityID(map['id'] as string),
    );
  }
}
